import { useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

/* ─── Design Tokens ──────────────────────────────────────────── */
const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

/* ─── Helpers ────────────────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('en-AE', { style: 'decimal', maximumFractionDigits: 0 }).format(n)

const fmtPct = (n) =>
  new Intl.NumberFormat('en-AE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n)

/* ─── Sub-components ─────────────────────────────────────────── */
const Label = ({ children }) => (
  <label style={{
    display: 'block', fontFamily: FONT, fontSize: '13px',
    fontWeight: 600, color: NAVY, marginBottom: '8px',
  }}>
    {children}
  </label>
)

const Field = ({ label, children, hint }) => (
  <div style={{ marginBottom: '22px' }}>
    <Label>{label}</Label>
    {children}
    {hint && (
      <p style={{ fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', margin: '5px 0 0' }}>
        {hint}
      </p>
    )}
  </div>
)

const inputStyle = {
  width: '100%', padding: '13px 16px',
  border: '1.5px solid #dde2ea',
  borderRadius: '10px', fontFamily: FONT, fontSize: '14px', color: NAVY,
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
}

const ResultCard = ({ label, value, highlight, sub }) => (
  <div style={{
    background: highlight ? NAVY : '#f4f7fb',
    borderRadius: '14px', padding: '20px 24px',
    borderLeft: `4px solid ${highlight ? GOLD : 'transparent'}`,
    transition: 'transform 0.2s',
  }}>
    <div style={{
      fontFamily: FONT, fontSize: '11px', fontWeight: 600,
      color: highlight ? 'rgba(255,255,255,0.6)' : '#8a9ab0',
      textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '8px',
    }}>
      {label}
    </div>
    <div style={{
      fontFamily: FONT, fontSize: highlight ? '28px' : '22px', fontWeight: 700,
      color: highlight ? '#fff' : NAVY, lineHeight: 1.15,
    }}>
      {value}
    </div>
    {sub && (
      <div style={{ fontFamily: FONT, fontSize: '12px', color: highlight ? 'rgba(255,255,255,0.5)' : '#8a9ab0', marginTop: '4px' }}>
        {sub}
      </div>
    )}
  </div>
)

/* ─── Main Calculator ─────────────────────────────────────────── */
function ROICalculator() {
  const [propertyPrice, setPropertyPrice]   = useState(1500000)
  const [annualRent, setAnnualRent]         = useState(90000)
  const [serviceCharge, setServiceCharge]   = useState(15000)
  const [agencyFee, setAgencyFee]           = useState(0)
  const [dldFee, setDldFee]                 = useState(60000)     // 4% of price
  const [appreciation, setAppreciation]     = useState(8)          // % per year
  const [holdingYears, setHoldingYears]     = useState(5)

  /* — Derived numbers — */
  const totalAcquisitionCost  = propertyPrice + agencyFee + dldFee
  const netAnnualRent         = annualRent - serviceCharge
  const grossYield            = propertyPrice > 0 ? (annualRent / propertyPrice) * 100 : 0
  const netYield              = totalAcquisitionCost > 0 ? (netAnnualRent / totalAcquisitionCost) * 100 : 0

  /* Capital gain over holding period */
  const projectedValue        = propertyPrice * Math.pow(1 + appreciation / 100, holdingYears)
  const capitalGain           = projectedValue - propertyPrice
  const totalRentalIncome     = netAnnualRent * holdingYears
  const totalReturn           = capitalGain + totalRentalIncome
  const totalROI              = totalAcquisitionCost > 0 ? (totalReturn / totalAcquisitionCost) * 100 : 0
  const annualisedROI         = holdingYears > 0 ? totalROI / holdingYears : 0

  const rangeStyle = (val, min, max) => ({
    width: '100%',
    WebkitAppearance: 'none',
    appearance: 'none',
    height: '5px',
    borderRadius: '3px',
    outline: 'none',
    cursor: 'pointer',
    background: `linear-gradient(to right, ${NAVY} ${((val - min) / (max - min)) * 100}%, #dde2ea 0%)`,
  })

  return (
    <>
      <style>{`
        .roi-input:focus { border-color: ${NAVY} !important; box-shadow: 0 0 0 3px rgba(0,46,83,0.1) !important; }
        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none; width: 20px; height: 20px;
          border-radius: 50%; background: ${NAVY};
          border: 3px solid #fff; box-shadow: 0 2px 8px rgba(0,46,83,0.35); cursor: pointer;
        }
        @media (max-width: 960px)  { .roi-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 640px)  { .roi-result-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div className="roi-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '32px', alignItems: 'start' }}>

        {/* ── LEFT: Inputs ── */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px 36px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', borderTop: `4px solid ${NAVY}` }}>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 26px' }}>
            Property &amp; Investment Details
          </h3>

          {/* Property Price */}
          <Field label="Property Purchase Price" hint="Total asking / purchase price in AED">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>AED {fmt(propertyPrice)}</span>
            </div>
            <input
              type="range" className="roi-slider" min={200000} max={20000000} step={50000}
              value={propertyPrice} onChange={e => setPropertyPrice(+e.target.value)}
              style={rangeStyle(propertyPrice, 200000, 20000000)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>200K</span><span>20M</span>
            </div>
          </Field>

          {/* Annual Rent */}
          <Field label="Expected Annual Rental Income" hint="Gross rent per year in AED">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>AED {fmt(annualRent)}</span>
            </div>
            <input
              type="range" className="roi-slider" min={0} max={1000000} step={5000}
              value={annualRent} onChange={e => setAnnualRent(+e.target.value)}
              style={rangeStyle(annualRent, 0, 1000000)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>0</span><span>1M</span>
            </div>
          </Field>

          {/* Service Charge */}
          <Field label="Annual Service Charge / Expenses" hint="RERA service charges, maintenance, insurance (AED/yr)">
            <input
              type="number" min={0} className="roi-input"
              value={serviceCharge} onChange={e => setServiceCharge(+e.target.value)}
              style={inputStyle}
            />
          </Field>

          {/* DLD Fee */}
          <Field label="DLD Transfer Fee" hint="Dubai Land Department fee — typically 4% of purchase price">
            <input
              type="number" min={0} className="roi-input"
              value={dldFee} onChange={e => setDldFee(+e.target.value)}
              style={inputStyle}
            />
          </Field>

          {/* Agency Fee */}
          <Field label="Agency / Registration Fees" hint="Brokerage commission and admin costs (AED)">
            <input
              type="number" min={0} className="roi-input"
              value={agencyFee} onChange={e => setAgencyFee(+e.target.value)}
              style={inputStyle}
            />
          </Field>

          {/* Appreciation */}
          <Field label="Expected Annual Capital Appreciation">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>{fmtPct(appreciation)}% per year</span>
            </div>
            <input
              type="range" className="roi-slider" min={0} max={25} step={0.5}
              value={appreciation} onChange={e => setAppreciation(+e.target.value)}
              style={rangeStyle(appreciation, 0, 25)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>0%</span><span>25%</span>
            </div>
          </Field>

          {/* Holding Period */}
          <Field label="Investment Holding Period">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>{holdingYears} year{holdingYears !== 1 ? 's' : ''}</span>
            </div>
            <input
              type="range" className="roi-slider" min={1} max={20} step={1}
              value={holdingYears} onChange={e => setHoldingYears(+e.target.value)}
              style={rangeStyle(holdingYears, 1, 20)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>1 yr</span><span>20 yrs</span>
            </div>
          </Field>
        </div>

        {/* ── RIGHT: Results ── */}
        <div>
          {/* Formula notice banner */}
          <div style={{
            background: `linear-gradient(135deg, ${GOLD}18 0%, ${GOLD}08 100%)`,
            border: `1px solid ${GOLD}40`,
            borderRadius: '14px', padding: '16px 20px', marginBottom: '20px',
            display: 'flex', gap: '12px', alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '22px', flexShrink: 0 }}>🔄</span>
            <div>
              <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '4px' }}>
                Formula Refinement Pending
              </div>
              <div style={{ fontFamily: FONT, fontSize: '12px', color: '#5a6577', lineHeight: 1.6 }}>
                Results are based on standard real estate ROI methodology. Your advisor will apply market-adjusted formulas once finalised. Use these figures as directional estimates only.
              </div>
            </div>
          </div>

          {/* Key metric */}
          <div style={{ marginBottom: '14px' }}>
            <ResultCard
              label="Estimated Annualised ROI"
              value={`${fmtPct(annualisedROI)}%`}
              highlight
              sub={`Over ${holdingYears} year${holdingYears !== 1 ? 's' : ''} holding period`}
            />
          </div>

          {/* 2-col grid */}
          <div className="roi-result-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <ResultCard label="Gross Rental Yield"    value={`${fmtPct(grossYield)}%`}            sub="Annual rent / purchase price" />
            <ResultCard label="Net Rental Yield"      value={`${fmtPct(netYield)}%`}              sub="After service charges &amp; fees" />
            <ResultCard label="Net Annual Rent"       value={`AED ${fmt(netAnnualRent)}`}          sub="Gross rent – expenses" />
            <ResultCard label="Total Acquisition Cost" value={`AED ${fmt(totalAcquisitionCost)}`} sub="Price + DLD + agency" />
            <ResultCard label="Projected Value"       value={`AED ${fmt(projectedValue)}`}         sub={`After ${holdingYears} yr appreciation`} />
            <ResultCard label="Projected Capital Gain" value={`AED ${fmt(capitalGain)}`}          sub="Value growth only" />
          </div>

          {/* Total return bar */}
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,46,83,0.07)' }}>
            <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#8a9ab0', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' }}>
              Return Breakdown (over {holdingYears} yr{holdingYears !== 1 ? 's' : ''})
            </div>

            {/* Stacked bar */}
            {totalReturn > 0 && (
              <>
                <div style={{ height: '12px', borderRadius: '6px', overflow: 'hidden', display: 'flex', marginBottom: '12px', background: '#e2e8f0' }}>
                  <div style={{
                    width: `${(capitalGain / totalReturn) * 100}%`,
                    background: NAVY, transition: 'width 0.5s ease',
                    minWidth: capitalGain > 0 ? '4px' : '0',
                  }} />
                  <div style={{
                    flex: 1, background: GOLD, transition: 'flex 0.5s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: '12px', color: '#5a6577' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: NAVY, display: 'inline-block', flexShrink: 0 }} />
                    Capital Gain ({fmtPct(capitalGain / totalReturn * 100)}%)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: '12px', color: '#5a6577' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: GOLD, display: 'inline-block', flexShrink: 0 }} />
                    Rental Income ({fmtPct(totalRentalIncome / totalReturn * 100)}%)
                  </div>
                </div>
              </>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '14px 0 0', marginTop: '12px',
              borderTop: '1px solid #f0f3f7',
            }}>
              <span style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#5a6577' }}>Total Projected Return</span>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: NAVY }}>AED {fmt(totalReturn)}</span>
            </div>
          </div>

          {/* Disclaimer */}
          <p style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', marginTop: '14px', lineHeight: 1.6 }}>
            * All figures are indicative projections based on inputs provided. Actual returns depend on market conditions, occupancy rates, financing costs, and regulatory changes. Consult a Victory Homes advisor for a personalised investment analysis.
          </p>
        </div>
      </div>
    </>
  )
}

/* ─── Page Shell ─────────────────────────────────────────────── */
function ROICalculatorPage() {
  return (
    <div className="website-wrapper wpresidence_wrapper_for_header_type1 wide" id="all_wrapper">
      <div className="container-fluid px-0 wpresidence_main_wrapper_class wide has_header_type1">
        <Navbar />
        <main style={{ background: '#f4f7fb', minHeight: '80vh' }}>

          {/* ── Hero Banner ── */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 60%, #0a2d4a 100%)`,
            padding: '150px 20px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}>
            {/* Decorative orbs */}
            <div style={{ position: 'absolute', top: '-100px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: `${GOLD}12`, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '280px', height: '280px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />

            <span style={{
              display: 'inline-block', background: `${GOLD}20`, color: GOLD,
              fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px',
              padding: '6px 18px', borderRadius: '50px', marginBottom: '18px',
              textTransform: 'uppercase', border: `1px solid ${GOLD}40`,
            }}>
              Investment Tools
            </span>

            <h1 style={{
              fontFamily: FONT, fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 700,
              color: '#fff', margin: '0 0 16px', letterSpacing: '-0.5px', lineHeight: 1.2,
            }}>
              Dubai Property<br />
              <span style={{ color: GOLD }}>ROI Calculator</span>
            </h1>

            <p style={{
              fontFamily: FONT, fontSize: '16px', color: 'rgba(255,255,255,0.70)',
              maxWidth: '560px', margin: '0 auto 36px', lineHeight: 1.7,
            }}>
              Estimate rental yields, capital appreciation, and total return on your Dubai real estate investment — before you commit a single dirham.
            </p>

            {/* Stats row */}
            <div style={{
              display: 'inline-flex', gap: '24px',
              background: 'rgba(255,255,255,0.07)',
              border: `1px solid ${GOLD}30`,
              borderRadius: '50px', padding: '10px 24px',
              flexWrap: 'wrap', justifyContent: 'center',
            }}>
              {[
                ['5–8%', 'Avg. Net Yield, Dubai'],
                ['30%+', 'Cap Growth since 2020'],
                ['0%', 'Capital Gains Tax'],
              ].map(([val, label]) => (
                <div key={label} style={{ fontFamily: FONT, fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                  <span style={{ color: GOLD, fontWeight: 700 }}>{val}</span>{' '}{label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Calculator ── */}
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '52px 20px 72px', boxSizing: 'border-box' }}>
            <ROICalculator />

            {/* ── How It Works ── */}
            <div style={{ marginTop: '64px' }}>
              <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                <span style={{
                  display: 'inline-block', background: `${GOLD}15`, color: GOLD,
                  fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px',
                  padding: '5px 16px', borderRadius: '50px', marginBottom: '12px', textTransform: 'uppercase',
                }}>How It Works</span>
                <h2 style={{ fontFamily: FONT, fontSize: '30px', fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
                  What Goes Into Your ROI
                </h2>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', maxWidth: '540px', margin: '0 auto', lineHeight: 1.7 }}>
                  Every figure in this calculator maps to a real cost or income stream in a Dubai investment.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
                {[
                  { icon: '🏷️', title: 'Gross Rental Yield',    desc: 'Annual rent divided by purchase price. A quick benchmark — Dubai averages 5–8% depending on area and asset type.' },
                  { icon: '📉', title: 'Net Rental Yield',      desc: 'Gross yield minus operating costs (service charges, insurance, voids). The real number you deposit into your bank.' },
                  { icon: '📈', title: 'Capital Appreciation',  desc: 'Property value growth over the holding period. Dubai off-plan and ready units have shown 30–60% growth since 2020.' },
                  { icon: '💼', title: 'Total ROI',             desc: 'Rental income + capital gain as a percentage of your total acquisition cost. The number that matters most to investors.' },
                ].map((item, i) => (
                  <div key={i}
                    style={{
                      background: '#fff', borderRadius: '14px', padding: '26px 26px',
                      boxShadow: '0 2px 14px rgba(0,46,83,0.06)',
                      borderBottom: '3px solid transparent',
                      transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,46,83,0.12)'; e.currentTarget.style.borderColor = GOLD }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,46,83,0.06)'; e.currentTarget.style.borderColor = 'transparent' }}
                  >
                    <div style={{ fontSize: '30px', marginBottom: '12px' }}>{item.icon}</div>
                    <h4 style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>{item.title}</h4>
                    <p style={{ fontFamily: FONT, fontSize: '13px', color: '#5a6577', margin: 0, lineHeight: 1.65 }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── CTA Banner ── */}
            <div style={{
              marginTop: '50px',
              background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
              borderRadius: '20px', padding: '48px 40px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: '24px',
            }}>
              <div>
                <div style={{ fontFamily: FONT, fontSize: '24px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                  Want a personalised investment analysis?
                </div>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.6, maxWidth: '460px' }}>
                  Our advisors will run the numbers on specific projects, factor in current market data, and find the best-yield properties for your budget.
                </p>
              </div>
              <a href="/contact"
                style={{
                  display: 'inline-block', background: GOLD, color: '#fff',
                  fontFamily: FONT, fontSize: '15px', fontWeight: 700,
                  padding: '15px 36px', borderRadius: '10px', textDecoration: 'none',
                  whiteSpace: 'nowrap', transition: 'background 0.2s, transform 0.15s',
                  boxShadow: `0 6px 20px rgba(197,160,89,0.4)`,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#d4af6a'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.transform = 'translateY(0)' }}
              >
                Speak to an Advisor →
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default ROICalculatorPage
